import { useEffect, useRef } from 'react';
import loadable from '@loadable/component';

import { isSWSGQLError } from '@/utilities/graphQLClient';

import track, { useTracking } from 'react-tracking';
import { Row } from '@simplywallst/ui-core';
import Footer from '@/components/Footer';
import { PlatformArticleLink, NewsArticleLink } from './LinkComponent';

import { useArticleSlug } from '../hooks/useArticleSlug';
import { useArticle, validateMarketArticle } from '../hooks/useArticle';
import Header from './Header';
import Metadata from './Metadata';
import ArticleMetadata from './ArticleMetadata';
import Body from './Body';
import ArticleSkeleton from './ArticleSkeleton';
import {
  Main,
  InnerContainer,
  MainContainer,
  LogoCol,
  BodyCol,
  SidebarCol,
} from './styled';
import AuthorWidget from '@/components/AuthorWidget';
import { RecommendedArticles } from './RecommendedArticles';
import ScrollTracking from '@/components/ScrollTracking';

const NotFound = loadable(() => import('@/pages/NotFound'));
const Oops = loadable(() => import('@components/Oops'));
const SharingSocialButtons = loadable(() => import('./SharingSocialButtons'), {
  ssr: false,
});
const ArticleComment = loadable(() => import('@/components/ArticleComment'), {
  ssr: false,
});

const Article = () => {
  const slug = useArticleSlug();
  const { data, isFetching, isError, error } = useArticle({ slug });
  const { trackEvent } = useTracking();
  const scrollCommentRef = useRef<null | HTMLDivElement>(null);

  // This hook is to scroll the article page to the top
  // when navigating from the middle of other pages,
  // e.g: market insight or another article page.
  useEffect(() => {
    if (slug && !window.location.href.includes('#')) {
      window.scrollTo(0, 0);
    }
    // tracking when user landed to a new article - when slug is changed
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'landed',
      slug,
    });
  }, [slug]);

  if (isFetching) {
    return (
      <Main>
        <ArticleSkeleton />
      </Main>
    );
  }

  if (isError && isSWSGQLError(error)) {
    if (error.code === '404') {
      return <NotFound />;
    }
    return <Oops />;
  }

  if (typeof data === 'undefined') {
    return <Oops />;
  }

  if (!validateMarketArticle(data?.article)) {
    return <NotFound />;
  }
  const { article } = data;
  const onCommentsClick = () => {
    scrollCommentRef?.current &&
      scrollCommentRef.current.scrollIntoView({ behavior: 'smooth' });
    trackEvent({
      type: 'track',
      subject: 'comments-link',
      action: 'click',
      slug,
    });
  };
  return (
    <>
      <ArticleMetadata article={article} />
      <Main role="main">
        <article>
          <Row justifyContent="center">
            <InnerContainer>
              <Header title={article.title} canonicalUrl={article.canonicalURL}>
                {article.author && (
                  <Metadata
                    name={article.author.name}
                    slug={article.author.slug}
                    photoURL={article.author.photoURL}
                    publishDate={article.created}
                    updatedDate={article.updated}
                    viewCommentsText="View comments"
                    reviewedBy={article.reviewedBy}
                    onCommentsClick={onCommentsClick}
                  />
                )}
              </Header>
            </InnerContainer>
          </Row>
          <MainContainer>
            <LogoCol />
            <BodyCol data-cy-id="page-article">
              <Body article={article} />
              <SharingSocialButtons />
              <ArticleComment
                canonicalURL={article.canonicalURL}
                scrollCommentRef={scrollCommentRef}
              />
            </BodyCol>
            <SidebarCol colLg={3}>
              {article.author && (
                <AuthorWidget
                  name={article.author?.name}
                  slug={article.author?.slug as string}
                  photoURL={article.author?.photoURL}
                  bio={article.author?.bio}
                />
              )}
              <RecommendedArticles
                articles={article.recommendedMarketArticles}
                articleType="MarketInsights"
                linkComponent={PlatformArticleLink}
              />
              <RecommendedArticles
                articles={article.recommendedHumanArticles}
                articleType="StockAnalysis"
                linkComponent={NewsArticleLink}
              />
            </SidebarCol>
          </MainContainer>
        </article>
        <ScrollTracking
          properties={{
            slug: article.title,
            id: article.metadata?.blueprintId,
          }}
          subject="article-footer"
        />
      </Main>
      <InnerContainer>
        <Footer />
      </InnerContainer>
    </>
  );
};

export default track({ page: 'ArticlePage' })(Article) as typeof Article;
